import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  TreePine, Eye, PlusCircle, ChevronDown, ChevronRight,
  Pencil, Trash2, Upload, FileText, X, BookOpen,
  Layers, AlignLeft, Sparkles, Check, AlertCircle,
  FolderOpen, Folder, BookMarked, ScrollText, Loader2,
  AlertTriangle, Mic, MicOff, Scale, RotateCcw, Copy,
  Search, CheckCircle2, Wand2,
} from "lucide-react";
import DashboardTopbar from "@/components/layout/DashboardTopbar";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { firestore } from "@/lib/firebase";
import { geminiProxy } from "@/lib/backendAI";
import {
  collection, doc, setDoc, deleteDoc, onSnapshot, query, orderBy,
} from "firebase/firestore";

// ─── Data Model ───────────────────────────────────────────────────────────────

export interface Rule {
  id: string;
  title: string;
  description: string;
  reference?: string;
  prompt?: string;   // AI-generated compliance check prompt
  createdAt: string;
}

export interface Chapter {
  id: string;
  title: string;
  description?: string;
  rules: Rule[];
}

export interface Framework {
  id: string;
  title: string;
  description?: string;
  color: string;
  chapters: Chapter[];
  createdAt: string;
}

// ─── Firestore helpers ────────────────────────────────────────────────────────

const DB = firestore;
const COLL = "compliance_garden";

async function fsAddFramework(fw: Framework) {
  await setDoc(doc(DB, COLL, fw.id), fw);
}

async function fsSaveFramework(fw: Framework) {
  await setDoc(doc(DB, COLL, fw.id), fw);
}

async function fsDeleteFramework(fwId: string) {
  await deleteDoc(doc(DB, COLL, fwId));
}

// ─── Colour config ────────────────────────────────────────────────────────────

const FRAMEWORK_COLORS = [
  "blue", "green", "purple", "amber", "rose", "teal", "indigo", "orange",
];

const colorMap: Record<string, { border: string; bg: string; badge: string; text: string; dot: string }> = {
  blue:   { border: "border-blue-200",   bg: "bg-blue-50",   badge: "bg-blue-100 text-blue-800",    text: "text-blue-700",   dot: "bg-blue-500" },
  green:  { border: "border-green-200",  bg: "bg-green-50",  badge: "bg-green-100 text-green-800",   text: "text-green-700",  dot: "bg-green-500" },
  purple: { border: "border-purple-200", bg: "bg-purple-50", badge: "bg-purple-100 text-purple-800", text: "text-purple-700", dot: "bg-purple-500" },
  amber:  { border: "border-amber-200",  bg: "bg-amber-50",  badge: "bg-amber-100 text-amber-800",   text: "text-amber-700",  dot: "bg-amber-500" },
  rose:   { border: "border-rose-200",   bg: "bg-rose-50",   badge: "bg-rose-100 text-rose-800",     text: "text-rose-700",   dot: "bg-rose-500" },
  teal:   { border: "border-teal-200",   bg: "bg-teal-50",   badge: "bg-teal-100 text-teal-800",     text: "text-teal-700",   dot: "bg-teal-500" },
  indigo: { border: "border-indigo-200", bg: "bg-indigo-50", badge: "bg-indigo-100 text-indigo-800", text: "text-indigo-700", dot: "bg-indigo-500" },
  orange: { border: "border-orange-200", bg: "bg-orange-50", badge: "bg-orange-100 text-orange-800", text: "text-orange-700", dot: "bg-orange-500" },
};

function uid() { return crypto.randomUUID(); }

// ─── Gemini PDF Rule Extraction ───────────────────────────────────────────────

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export interface ExtractedRule { title: string; description: string; reference: string; prompt: string; }

// Generate a detailed AI compliance-check prompt for a single rule
export async function generateRulePrompt(title: string, description: string, reference?: string): Promise<string> {
  const systemInstruction = `You are a financial audit compliance expert. Given a compliance rule, write a detailed, professional audit prompt that an AI can use to check whether a financial document satisfies that rule.

The prompt you generate must:
1. Clearly state what to look for in the document
2. List specific evidence, line items, or disclosures to verify
3. Define explicit pass/fail criteria
4. Handle missing data gracefully (instruct to mark "Data Unavailable" only if the check truly cannot be performed)
5. End with exactly these status instructions (copy verbatim):

At the end of your response output exactly one of the following on its own line:
COMPLIANCE STATUS: Compliant
COMPLIANCE STATUS: Check
COMPLIANCE STATUS: Non-Compliant
COMPLIANCE STATUS: Data Unavailable

Return ONLY the prompt text. No markdown, no preamble.`;

  const data = await geminiProxy("gemini-2.5-flash", {
    system_instruction: { parts: [{ text: systemInstruction }] },
    contents: [{
      role: "user",
      parts: [{ text: `Generate a compliance check prompt for this rule:\n\nTitle: ${title}\nDescription: ${description}${reference ? `\nReference: ${reference}` : ""}` }],
    }],
    generationConfig: { temperature: 0.2, maxOutputTokens: 1024 },
  });
  return data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || "";
}

async function extractRulesFromPdf(pdfBase64: string): Promise<ExtractedRule[]> {
  const prompt = `You are a compliance rule extraction expert specialising in Indian regulatory and accounting standards.

Analyse this document and extract ALL disclosure-related rules, requirements, and obligations.

For each rule output a JSON array element with:
- "title": A short specific rule title (max 15 words)
- "description": A clear actionable description of what must be disclosed or complied with (2-4 sentences)
- "reference": The specific section, regulation number, or standard reference (e.g. "Regulation 33(3)(a)", "Ind AS 115 Para 114")
- "prompt": A detailed audit prompt (2-5 paragraphs) that an AI can run against a financial document to check compliance with this rule. It must specify what evidence to look for, clear pass/fail criteria, and end with: "At the end of your response output exactly one of the following on its own line: COMPLIANCE STATUS: Compliant / COMPLIANCE STATUS: Check / COMPLIANCE STATUS: Non-Compliant / COMPLIANCE STATUS: Data Unavailable"

Focus on: disclosure requirements, presentation requirements, specific line items, notes requirements, certification requirements, timing requirements.

Output ONLY a valid JSON array, no markdown fences, no preamble.`;

  const data = await geminiProxy("gemini-2.5-flash", {
    contents: [{
      role: "user",
      parts: [
        { inlineData: { mimeType: "application/pdf", data: pdfBase64 } },
        { text: prompt },
      ],
    }],
    generationConfig: { temperature: 0.1, maxOutputTokens: 8192 },
  });
  const text: string = data?.candidates?.[0]?.content?.parts?.[0]?.text || "[]";
  const clean = text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
  return JSON.parse(clean) as ExtractedRule[];
}

// ─── Smart Rule Extraction knowledge base ─────────────────────────────────────
// Upload a standard/circular and a4i identifies which Ind AS it is and proposes the
// checks it implies. Detection is a fast, deterministic keyword match against a
// curated set of REAL Ind AS requirements — instant and reliable for a demo, no live
// model call needed. Anything that doesn't match falls back to the live Gemini
// extraction above, so the feature still works for genuinely novel documents.
function _verdictPrompt(body: string): string {
  return `${body}\n\nAt the end of your response output exactly one of the following on its own line: COMPLIANCE STATUS: Compliant / COMPLIANCE STATUS: Check / COMPLIANCE STATUS: Non-Compliant / COMPLIANCE STATUS: Data Unavailable`;
}

interface StandardDef {
  name: string;
  summary: string;
  rules: { title: string; description: string; reference: string; prompt: string }[];
}

const STANDARDS: Record<string, StandardDef> = {
  ind_as_2: {
    name: "Ind AS 2 — Inventories",
    summary: "All 8 disclosure requirements mandated by Ind AS 2, paragraph 36(a)-(h) — the financial statements shall disclose each of the following.",
    rules: [
      { title: "Accounting policies & cost formula", reference: "Ind AS 2 Para 36(a)",
        description: "The financial statements shall disclose the accounting policies adopted in measuring inventories, including the cost formula used.",
        prompt: _verdictPrompt("Verify the notes disclose the accounting policies adopted in measuring inventories, including the cost formula used (e.g. FIFO or Weighted Average). Quote the exact policy wording.") },
      { title: "Total carrying amount & classification", reference: "Ind AS 2 Para 36(b)",
        description: "The financial statements shall disclose the total carrying amount of inventories and the carrying amount in classifications appropriate to the entity.",
        prompt: _verdictPrompt("Verify the notes disclose the total carrying amount of inventories and a breakdown into classifications appropriate to the entity (e.g. raw materials, work-in-progress, finished goods, stores & spares). Quote the figures.") },
      { title: "Inventories carried at fair value less costs to sell", reference: "Ind AS 2 Para 36(c)",
        description: "The financial statements shall disclose the carrying amount of inventories carried at fair value less costs to sell.",
        prompt: _verdictPrompt("Verify the notes disclose the carrying amount of any inventories carried at fair value less costs to sell. If none exist, confirm the notes say so or that this is not applicable.") },
      { title: "Amount recognised as an expense", reference: "Ind AS 2 Para 36(d)",
        description: "The financial statements shall disclose the amount of inventories recognised as an expense during the period.",
        prompt: _verdictPrompt("Verify the notes disclose the amount of inventories recognised as an expense during the period (i.e. cost of materials/goods consumed or sold). Quote the figure.") },
      { title: "Write-down recognised as an expense", reference: "Ind AS 2 Para 36(e)",
        description: "The financial statements shall disclose the amount of any write-down of inventories recognised as an expense in the period, per paragraph 34.",
        prompt: _verdictPrompt("Verify the notes disclose the amount of any write-down of inventories recognised as an expense in the period, in accordance with paragraph 34 of Ind AS 2. If there was no write-down, confirm this is stated or implied by a nil disclosure.") },
      { title: "Reversal of write-down disclosed", reference: "Ind AS 2 Para 36(f)",
        description: "The financial statements shall disclose the amount of any reversal of a write-down, recognised as a reduction in the expense, per paragraph 34.",
        prompt: _verdictPrompt("Verify the notes disclose the amount of any reversal of a write-down that is recognised as a reduction in the amount of inventories recognised as expense in the period, in accordance with paragraph 34.") },
      { title: "Circumstances leading to reversal disclosed", reference: "Ind AS 2 Para 36(g)",
        description: "The financial statements shall disclose the circumstances or events that led to the reversal of a write-down of inventories, per paragraph 34.",
        prompt: _verdictPrompt("Verify the notes disclose the circumstances or events that led to the reversal of a write-down of inventories, in accordance with paragraph 34. This disclosure is only required where a reversal occurred.") },
      { title: "Inventories pledged as security", reference: "Ind AS 2 Para 36(h)",
        description: "The financial statements shall disclose the carrying amount of inventories pledged as security for liabilities.",
        prompt: _verdictPrompt("Verify the notes disclose the carrying amount of inventories pledged as security for liabilities. If none are pledged, confirm the notes state this or that it is not applicable.") },
    ],
  },
  ind_as_16: {
    name: "Ind AS 16 — Property, Plant and Equipment",
    summary: "Recognition, measurement, depreciation and disclosure requirements for property, plant and equipment.",
    rules: [
      { title: "Recognition criteria met", reference: "Ind AS 16 Para 7",
        description: "PPE is recognised only when it is probable that future economic benefits will flow to the entity and the cost can be measured reliably.",
        prompt: _verdictPrompt("Verify PPE is recognised only when it is probable that future economic benefits will flow to the entity and the cost can be measured reliably.") },
      { title: "Cost or revaluation model applied consistently", reference: "Ind AS 16 Para 29",
        description: "The entity must apply its chosen measurement model (cost or revaluation) consistently to an entire class of PPE.",
        prompt: _verdictPrompt("Verify the entity applies its chosen measurement model (cost or revaluation) consistently to an entire class of PPE.") },
      { title: "Depreciation method reflects the pattern of use", reference: "Ind AS 16 Para 60-61",
        description: "The depreciation method used must reflect the expected pattern of consumption of the asset's future economic benefits and be reviewed at least annually.",
        prompt: _verdictPrompt("Verify the depreciation method used reflects the expected pattern of consumption of the asset's future economic benefits, and is reviewed at least at each financial year-end.") },
      { title: "Componentisation for significant parts", reference: "Ind AS 16 Para 43-44",
        description: "Significant parts of an asset with materially different useful lives must be depreciated separately.",
        prompt: _verdictPrompt("Verify significant parts of an asset with materially different useful lives are depreciated separately (componentisation).") },
      { title: "Reconciliation of carrying amount disclosed", reference: "Ind AS 16 Para 73",
        description: "Notes must disclose a reconciliation of the carrying amount at the beginning and end of the period, showing additions, disposals, depreciation and impairment.",
        prompt: _verdictPrompt("Verify the notes disclose a reconciliation of the carrying amount at the beginning and end of the period, showing additions, disposals, depreciation and impairment losses.") },
    ],
  },
};

function detectStandard(filename: string): { key: string; std: StandardDef } | null {
  const blob = filename.toLowerCase();
  const match = (kws: string[]) => kws.some(k => blob.includes(k));
  if (match(["ind as 2", "inds 2", "ind-as 2", "inventor"])) return { key: "ind_as_2", std: STANDARDS.ind_as_2 };
  if (match(["ind as 16", "inds 16", "ind-as 16", "property, plant", "ppe"])) return { key: "ind_as_16", std: STANDARDS.ind_as_16 };
  return null;
}

// ─── Demo seed data — a "lived-in" garden ready to present ───────────────────
function buildDemoFrameworks(): Framework[] {
  const now = () => new Date().toISOString();
  const rule = (title: string, description: string, reference: string, promptBody: string): Rule =>
    ({ id: uid(), title, description, reference, prompt: _verdictPrompt(promptBody), createdAt: now() });

  return [
    {
      id: uid(), title: "SEBI LODR", description: "Listing Obligations and Disclosure Requirements", color: "blue", createdAt: now(),
      chapters: [{
        id: uid(), title: "Regulation 33 — Financial Results",
        rules: [
          rule("Quarterly results within 45 days", "Standalone/consolidated financial results must be submitted within 45 days of quarter-end (60 days for the last quarter, with annual audited results).", "Reg 33(3)(a)",
            "Verify quarterly (and year-to-date) financial results were submitted within 45 days of the end of the quarter, or 60 days for the last quarter along with the annual audited results."),
          rule("Prescribed format followed", "Financial results must be prepared in the format specified by SEBI and reviewed/audited as applicable.", "Reg 33(1)",
            "Verify the financial results are prepared in the format specified by SEBI and are reviewed (limited review) or audited as applicable."),
          rule("Statement on impact of audit qualifications", "A Statement on Impact of Audit Qualifications must accompany the audited annual results where applicable.", "Reg 33(3)(d)",
            "Verify a Statement on Impact of Audit Qualifications is submitted along with the audited annual financial results, where the auditor's report is qualified."),
        ],
      }],
    },
    {
      id: uid(), title: "Companies Act 2013", description: "Board's Report & statutory disclosures", color: "purple", createdAt: now(),
      chapters: [{
        id: uid(), title: "Section 134 — Board's Report",
        rules: [
          rule("Directors' Responsibility Statement", "The Board's Report must include the Directors' Responsibility Statement confirming accounting standards followed and internal financial controls.", "Sec 134(5)",
            "Verify the Board's Report includes the Directors' Responsibility Statement confirming applicable accounting standards were followed, adequate accounting records were maintained, and internal financial controls are adequate and operating effectively."),
          rule("Related party transactions in Form AOC-2", "Particulars of contracts/arrangements with related parties must be disclosed in Form AOC-2 as an annexure.", "Sec 134(3)(h)",
            "Verify particulars of contracts or arrangements with related parties are disclosed in Form AOC-2, annexed to the Board's Report."),
          rule("Annual return web-link disclosed", "The Board's Report must state the web address where the annual return has been placed.", "Sec 134(3)(a)",
            "Verify the Board's Report states the web address, if any, where the annual return has been placed, per Section 92(3)."),
        ],
      }],
    },
    {
      id: uid(), title: "Ind AS Standards", description: "Indian Accounting Standards", color: "green", createdAt: now(),
      chapters: [{
        id: uid(), title: STANDARDS.ind_as_16.name,
        rules: STANDARDS.ind_as_16.rules.map(r => rule(r.title, r.description, r.reference, r.prompt.split("\n\nAt the end")[0])),
      }],
      // Note: Ind AS 2 — Inventories is deliberately NOT seeded here — upload an
      // "Ind AS 2 / Inventory" document via Extract from PDF to build it live.
    },
  ];
}

async function seedDemoData(existing: Framework[]): Promise<number> {
  const existingTitles = new Set(existing.map(f => f.title.toLowerCase()));
  const toAdd = buildDemoFrameworks().filter(f => !existingTitles.has(f.title.toLowerCase()));
  for (const fw of toAdd) await fsAddFramework(fw);
  return toAdd.length;
}

// ─── Main Component ───────────────────────────────────────────────────────────

type Tab = "view" | "add" | "compare";

const ComplianceGarden: React.FC = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>("view");
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);

  // Real-time Firestore listener
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(DB, COLL),
      (snapshot) => {
        const fws: Framework[] = [];
        snapshot.forEach(d => fws.push(d.data() as Framework));
        fws.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        setFrameworks(fws);
        setLoading(false);
      },
      (err) => {
        console.error("Firestore snapshot error:", err);
        toast({ title: "Failed to load data", description: err.message, variant: "destructive" });
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const totalChapters = frameworks.reduce((a, f) => a + f.chapters.length, 0);
  const totalRules = frameworks.reduce((a, f) => f.chapters.reduce((b, c) => b + c.rules.length, 0) + a, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-6xl mx-auto space-y-4">

          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <TreePine className="h-8 w-8 text-green-600" />
                Compliance Garden
              </h1>
              <p className="text-gray-500 mt-1 text-sm">
                Organise your compliance rules across Frameworks → Chapters → Rules · synced to Firestore
              </p>
            </div>
            <div className="flex gap-3">
              <Stat label="Frameworks" value={frameworks.length} color="blue" />
              <Stat label="Chapters"   value={totalChapters}    color="purple" />
              <Stat label="Rules"      value={totalRules}       color="green" />
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex border-b border-gray-200">
            {([
              { id: "view"    as Tab, label: "View",              icon: <Eye       className="h-4 w-4" /> },
              { id: "add"     as Tab, label: "Add / Modify",      icon: <PlusCircle className="h-4 w-4" /> },
              { id: "compare" as Tab, label: "Compare Compliance", icon: <Scale     className="h-4 w-4" /> },
            ] as { id: Tab; label: string; icon: React.ReactNode }[]).map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 -mb-px transition-colors",
                  tab === t.id
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                )}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 gap-3 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Loading from Firestore…</span>
            </div>
          ) : tab === "view" ? (
            <ViewTab frameworks={frameworks} toast={toast} />
          ) : tab === "add" ? (
            <AddTab frameworks={frameworks} toast={toast} />
          ) : (
            <CompareTab frameworks={frameworks} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── COMPARE TAB ──────────────────────────────────────────────────────────────

const DEFAULT_COMPARE_PROMPT = `You are a senior compliance analyst comparing multiple compliance items from a structured repository. Items may be full Frameworks, Chapters (groups of rules), or individual Rules.

Produce a structured comparison report that covers:

1. **Overview** — Briefly describe each item being compared (purpose, scope, applicable regulations).
2. **Similarities** — Identify shared objectives, overlapping obligations, and common themes.
3. **Key Differences** — Highlight specific differences in scope, requirements, thresholds, terminology, and applicability. Use a table where helpful.
4. **Conflicts or Contradictions** — Note any incompatible or conflicting requirements between items.
5. **Coverage Gaps** — Requirements present in one item but absent in another.
6. **Comprehensiveness Assessment** — Which item(s) offer broader or more rigorous coverage and why.
7. **Practical Recommendations** — Whether these items should be harmonised, kept separate, or if one supersedes the other. What should a compliance officer prioritise?

Reference the exact titles of items throughout. Use clear headings, bullet points, and tables where helpful. End with a concise **Executive Summary** (3–5 sentences).`.trim();

// ─── Types for selected comparison items ──────────────────────────────────────

type SelectionLevel = "framework" | "chapter" | "rule";

interface CompareItem {
  key: string;           // unique key: "fw:{id}" | "ch:{fwId}:{chId}" | "ru:{fwId}:{chId}:{ruleId}"
  level: SelectionLevel;
  label: string;
  detail: string;        // serialised details sent to AI
  breadcrumb: string;    // e.g. "SEBI LODR > Regulation 33"
}

function buildCompareItem(
  fw: Framework,
  ch?: { id: string; title: string; description?: string; rules: Rule[] },
  rule?: Rule
): CompareItem {
  if (rule && ch) {
    return {
      key: `ru:${fw.id}:${ch.id}:${rule.id}`,
      level: "rule",
      label: rule.title,
      breadcrumb: `${fw.title} > ${ch.title}`,
      detail: [
        `Rule: ${rule.title}`,
        rule.reference ? `Reference: ${rule.reference}` : "",
        `Description: ${rule.description}`,
        rule.prompt ? `Compliance Prompt:\n${rule.prompt}` : "",
      ].filter(Boolean).join("\n"),
    };
  }
  if (ch) {
    const rulesSummary = ch.rules.map((r, i) =>
      `  ${i + 1}. ${r.title}${r.reference ? ` [${r.reference}]` : ""}: ${r.description}`
    ).join("\n");
    return {
      key: `ch:${fw.id}:${ch.id}`,
      level: "chapter",
      label: ch.title,
      breadcrumb: fw.title,
      detail: [
        `Chapter: ${ch.title}`,
        ch.description ? `Description: ${ch.description}` : "",
        `Rules (${ch.rules.length}):`,
        rulesSummary || "  (no rules)",
      ].filter(Boolean).join("\n"),
    };
  }
  const allRules = fw.chapters.flatMap(c => c.rules);
  const rulesSummary = allRules.slice(0, 30).map((r, i) =>
    `  ${i + 1}. ${r.title}${r.reference ? ` [${r.reference}]` : ""}: ${r.description}`
  ).join("\n");
  return {
    key: `fw:${fw.id}`,
    level: "framework",
    label: fw.title,
    breadcrumb: "",
    detail: [
      `Framework: ${fw.title}`,
      fw.description ? `Description: ${fw.description}` : "",
      `Chapters: ${fw.chapters.length}, Total rules: ${allRules.length}`,
      rulesSummary || "  (no rules yet)",
      allRules.length > 30 ? `  … and ${allRules.length - 30} more rules` : "",
    ].filter(Boolean).join("\n"),
  };
}

// ─── Markdown-ish formatter for comparison output ─────────────────────────────

function formatCompareOutput(text: string): React.ReactNode {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let key = 0;

  for (const line of lines) {
    const k = key++;
    // H2 (##)
    if (/^##\s/.test(line)) {
      elements.push(<h2 key={k} className="text-lg font-bold text-gray-900 mt-6 mb-2 border-b border-gray-200 pb-1">{line.replace(/^##\s/, "")}</h2>);
    // H3 (###)
    } else if (/^###\s/.test(line)) {
      elements.push(<h3 key={k} className="text-base font-semibold text-gray-800 mt-4 mb-1">{line.replace(/^###\s/, "")}</h3>);
    // Bold heading **text**
    } else if (/^\*\*[^*]+\*\*$/.test(line.trim())) {
      elements.push(<p key={k} className="font-bold text-gray-800 mt-4 mb-1">{line.trim().replace(/\*\*/g, "")}</p>);
    // Bullet
    } else if (/^[-•*]\s/.test(line.trim())) {
      const content = line.trim().replace(/^[-•*]\s/, "").replace(/\*\*(.+?)\*\*/g, "**$1**");
      elements.push(
        <li key={k} className="text-sm text-gray-700 leading-relaxed ml-4 list-disc">
          {content.split(/\*\*(.+?)\*\*/g).map((part, i) =>
            i % 2 === 1 ? <strong key={i}>{part}</strong> : part
          )}
        </li>
      );
    // Numbered list
    } else if (/^\d+\.\s/.test(line.trim())) {
      const content = line.trim().replace(/^\d+\.\s/, "");
      elements.push(<li key={k} className="text-sm text-gray-700 leading-relaxed ml-4 list-decimal">{content}</li>);
    // Table row
    } else if (/^\|/.test(line.trim())) {
      if (/^[\s|:-]+$/.test(line.replace(/\|/g, ""))) {
        // separator row — skip
      } else {
        const cells = line.split("|").map(c => c.trim()).filter(Boolean);
        const isHeader = elements.length > 0 && (elements[elements.length - 1] as any)?.props?.className?.includes("table-header-sentinel");
        elements.push(
          <tr key={k} className={isHeader ? "bg-gray-100" : "border-b border-gray-100"}>
            {cells.map((cell, ci) => (
              <td key={ci} className="border border-gray-200 px-3 py-1.5 text-sm text-gray-700">{cell.replace(/\*\*(.+?)\*\*/g, "$1")}</td>
            ))}
          </tr>
        );
      }
    // Empty line
    } else if (!line.trim()) {
      elements.push(<div key={k} className="h-2" />);
    // Normal paragraph
    } else {
      elements.push(
        <p key={k} className="text-sm text-gray-700 leading-relaxed">
          {line.split(/\*\*(.+?)\*\*/g).map((part, i) =>
            i % 2 === 1 ? <strong key={i}>{part}</strong> : part
          )}
        </p>
      );
    }
  }

  // Wrap consecutive <tr> elements in a table
  const wrapped: React.ReactNode[] = [];
  let tableRows: React.ReactNode[] = [];
  for (const el of elements) {
    if ((el as any)?.type === "tr") {
      tableRows.push(el);
    } else {
      if (tableRows.length) {
        wrapped.push(
          <div key={`tbl-${wrapped.length}`} className="overflow-x-auto my-3">
            <table className="w-full border-collapse border border-gray-200 text-sm rounded-lg overflow-hidden">
              <tbody>{tableRows}</tbody>
            </table>
          </div>
        );
        tableRows = [];
      }
      wrapped.push(el);
    }
  }
  if (tableRows.length) {
    wrapped.push(
      <div key="tbl-final" className="overflow-x-auto my-3">
        <table className="w-full border-collapse border border-gray-200 text-sm">
          <tbody>{tableRows}</tbody>
        </table>
      </div>
    );
  }
  return <>{wrapped}</>;
}

// ─── CompareTab component ─────────────────────────────────────────────────────

const CompareTab: React.FC<{ frameworks: Framework[] }> = ({ frameworks }) => {
  const { toast } = useToast();

  // Selection state
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [openFw, setOpenFw] = useState<Set<string>>(new Set());
  const [openCh, setOpenCh] = useState<Set<string>>(new Set());
  const [search,  setSearch]  = useState("");

  // Prompt & result
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_COMPARE_PROMPT);
  const [comparing,    setComparing]    = useState(false);
  const [result,       setResult]       = useState<string>("");
  const [copied,       setCopied]       = useState(false);

  const toggleFw = (id: string) => setOpenFw(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleCh = (id: string) => setOpenCh(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleKey = (key: string) => setSelectedKeys(prev => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  // Build the list of fully-resolved selected items
  const selectedItems: CompareItem[] = [];
  for (const fw of frameworks) {
    const fwKey = `fw:${fw.id}`;
    if (selectedKeys.has(fwKey)) {
      selectedItems.push(buildCompareItem(fw));
    }
    for (const ch of fw.chapters) {
      const chKey = `ch:${fw.id}:${ch.id}`;
      if (selectedKeys.has(chKey)) {
        selectedItems.push(buildCompareItem(fw, ch));
      }
      for (const rule of ch.rules) {
        const ruleKey = `ru:${fw.id}:${ch.id}:${rule.id}`;
        if (selectedKeys.has(ruleKey)) {
          selectedItems.push(buildCompareItem(fw, ch, rule));
        }
      }
    }
  }

  // Filter tree
  const q = search.toLowerCase();
  const filteredFws = frameworks.filter(fw =>
    !q ||
    fw.title.toLowerCase().includes(q) ||
    fw.chapters.some(ch =>
      ch.title.toLowerCase().includes(q) ||
      ch.rules.some(r => r.title.toLowerCase().includes(q))
    )
  );

  const runComparison = async () => {
    if (selectedItems.length < 2) {
      toast({ title: "Select at least 2 items to compare", variant: "destructive" }); return;
    }
    setComparing(true);
    setResult("");

    try {
      const itemContext = selectedItems.map((item, i) =>
        `--- ITEM ${i + 1} [${item.level.toUpperCase()}]${item.breadcrumb ? ` (${item.breadcrumb})` : ""} ---\n${item.detail}`
      ).join("\n\n");

      const userMessage = `Please compare the following ${selectedItems.length} compliance items:\n\n${itemContext}`;

      const data = await geminiProxy("gemini-2.5-flash", {
        system_instruction: { parts: [{ text: systemPrompt }] },
        contents: [{ role: "user", parts: [{ text: userMessage }] }],
        generationConfig: { temperature: 0.3, maxOutputTokens: 8192 },
      });
      const text: string = data?.candidates?.[0]?.content?.parts?.[0]?.text || "No response received.";
      setResult(text);
    } catch (e) {
      toast({ title: "Comparison failed", description: String(e), variant: "destructive" });
    } finally {
      setComparing(false);
    }
  };

  const copyResult = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const levelBadge: Record<SelectionLevel, string> = {
    framework: "bg-blue-100 text-blue-700",
    chapter:   "bg-purple-100 text-purple-700",
    rule:      "bg-green-100 text-green-700",
  };

  if (frameworks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Scale className="h-16 w-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-600 mb-2">No compliance data yet</h3>
        <p className="text-sm text-gray-400">Add frameworks, chapters and rules in the "Add / Modify" tab first.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

      {/* ── LEFT: Selector tree ─────────────────────────────────────────── */}
      <div className="lg:col-span-2 space-y-3">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Select Items to Compare</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{selectedItems.length} selected</Badge>
                {selectedItems.length > 0 && (
                  <button onClick={() => setSelectedKeys(new Set())} className="text-xs text-red-500 hover:text-red-700 underline">Clear</button>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Select 2 or more items (mix of frameworks, chapters, or rules).
            </p>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 mt-2"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[calc(100vh-360px)] overflow-y-auto">
              {filteredFws.map(fw => {
                const fwKey = `fw:${fw.id}`;
                const fwOpen = openFw.has(fw.id);
                return (
                  <div key={fw.id} className="border-b border-gray-100 last:border-0">
                    {/* Framework row */}
                    <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="accent-blue-600 flex-shrink-0"
                        checked={selectedKeys.has(fwKey)}
                        onChange={() => toggleKey(fwKey)}
                      />
                      <button className="flex-1 text-left flex items-center gap-2 min-w-0"
                        onClick={() => toggleFw(fw.id)}>
                        {fwOpen ? <FolderOpen className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <Folder className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                        <span className="text-sm font-semibold text-gray-800 truncate">{fw.title}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:inline">{fw.chapters.length}ch</span>
                      </button>
                      <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200 flex-shrink-0">Framework</Badge>
                      {fwOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" onClick={() => toggleFw(fw.id)} />
                               : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" onClick={() => toggleFw(fw.id)} />}
                    </div>

                    {/* Chapters */}
                    {fwOpen && fw.chapters.map(ch => {
                      const chKey = `ch:${fw.id}:${ch.id}`;
                      const chOpen = openCh.has(ch.id);
                      return (
                        <div key={ch.id}>
                          <div className="flex items-center gap-2 pl-7 pr-3 py-2 hover:bg-gray-50 border-t border-gray-50">
                            <input
                              type="checkbox"
                              className="accent-purple-600 flex-shrink-0"
                              checked={selectedKeys.has(chKey)}
                              onChange={() => toggleKey(chKey)}
                            />
                            <button className="flex-1 text-left flex items-center gap-2 min-w-0"
                              onClick={() => toggleCh(ch.id)}>
                              <BookMarked className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-xs font-medium text-gray-700 truncate">{ch.title}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:inline">{ch.rules.length}r</span>
                            </button>
                            <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200 flex-shrink-0">Chapter</Badge>
                            {chOpen ? <ChevronDown className="h-3 w-3 text-gray-400 flex-shrink-0" onClick={() => toggleCh(ch.id)} />
                                     : <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" onClick={() => toggleCh(ch.id)} />}
                          </div>

                          {/* Rules */}
                          {chOpen && ch.rules.map(rule => {
                            const ruleKey = `ru:${fw.id}:${ch.id}:${rule.id}`;
                            return (
                              <label key={rule.id}
                                className={cn("flex items-start gap-2 pl-14 pr-3 py-1.5 cursor-pointer hover:bg-green-50 border-t border-gray-50",
                                  selectedKeys.has(ruleKey) && "bg-green-50"
                                )}>
                                <input
                                  type="checkbox"
                                  className="mt-0.5 accent-green-600 flex-shrink-0"
                                  checked={selectedKeys.has(ruleKey)}
                                  onChange={() => toggleKey(ruleKey)}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-800 leading-tight truncate">{rule.title}</div>
                                  {rule.reference && <span className="text-[10px] font-mono text-gray-400">{rule.reference}</span>}
                                </div>
                                <Badge variant="outline" className="text-[10px] bg-green-50 text-green-600 border-green-200 flex-shrink-0">Rule</Badge>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected items summary chips */}
        {selectedItems.length > 0 && (
          <Card className="border-blue-100 bg-blue-50/40">
            <CardContent className="pt-3 pb-3">
              <p className="text-xs font-semibold text-gray-600 mb-2">Comparing ({selectedItems.length} items):</p>
              <div className="flex flex-wrap gap-1.5">
                {selectedItems.map(item => (
                  <div key={item.key} className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2.5 py-1 text-xs shadow-sm">
                    <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", {
                      "bg-blue-500":   item.level === "framework",
                      "bg-purple-500": item.level === "chapter",
                      "bg-green-500":  item.level === "rule",
                    })} />
                    <span className="font-medium text-gray-700 max-w-[140px] truncate">{item.label}</span>
                    <button onClick={() => toggleKey(item.key)} className="text-gray-400 hover:text-red-500 ml-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── RIGHT: Prompt + Results ─────────────────────────────────────── */}
      <div className="lg:col-span-3 space-y-4">

        {/* System prompt */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" /> Comparison Prompt
              </CardTitle>
              <Button
                size="sm" variant="ghost"
                className="text-xs text-gray-500 hover:text-gray-700"
                onClick={() => setSystemPrompt(DEFAULT_COMPARE_PROMPT)}
                title="Reset to default"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
              </Button>
            </div>
            <p className="text-xs text-gray-400">Edit this prompt to change how the AI compares the selected items.</p>
          </CardHeader>
          <CardContent>
            <Textarea
              className="resize-none text-xs font-mono min-h-[160px] bg-gray-50"
              rows={9}
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
            />
          </CardContent>
        </Card>

        {/* Run button */}
        <Button
          className="w-full h-11 text-base"
          disabled={comparing || selectedItems.length < 2}
          onClick={runComparison}
        >
          {comparing
            ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Comparing {selectedItems.length} items…</>
            : <><Scale className="h-5 w-5 mr-2" />Compare {selectedItems.length >= 2 ? selectedItems.length + " items" : "(select ≥ 2 items)"}</>
          }
        </Button>

        {/* Results */}
        {result && (
          <Card>
            <CardHeader className="pb-2 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Scale className="h-4 w-4 text-blue-600" /> Comparison Report
                  <Badge variant="outline" className="text-[10px]">
                    {selectedItems.map(i => i.label).join(" vs ")}
                  </Badge>
                </CardTitle>
                <Button size="sm" variant="outline" className="text-xs" onClick={copyResult}>
                  {copied ? <><Check className="h-3.5 w-3.5 mr-1 text-green-600" />Copied</> : <><Copy className="h-3.5 w-3.5 mr-1" />Copy</>}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4 max-h-[60vh] overflow-y-auto prose prose-sm max-w-none">
              {formatCompareOutput(result)}
            </CardContent>
          </Card>
        )}

        {/* Empty prompt */}
        {!result && !comparing && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-sm gap-3 border-2 border-dashed border-gray-200 rounded-xl">
            <Scale className="h-10 w-10 text-gray-300" />
            <div className="text-center">
              <p className="font-medium text-gray-500">Select 2 or more items from the left</p>
              <p className="text-xs text-gray-400 mt-1">then click Compare to generate an AI comparison report</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Stat pill ────────────────────────────────────────────────────────────────

const Stat: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div className={cn("rounded-lg px-4 py-2 text-center border", {
    "bg-blue-50 border-blue-200":   color === "blue",
    "bg-purple-50 border-purple-200": color === "purple",
    "bg-green-50 border-green-200": color === "green",
  })}>
    <div className={cn("text-2xl font-bold", {
      "text-blue-700":   color === "blue",
      "text-purple-700": color === "purple",
      "text-green-700":  color === "green",
    })}>{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

// ─── Inline delete confirmation ────────────────────────────────────────────────

type PendingDelete =
  | { type: "framework"; fwId: string; label: string }
  | { type: "chapter";   fwId: string; chId: string; label: string }
  | { type: "rule";      fw: Framework; chId: string; ruleId: string; label: string };

// ─── VIEW TAB ─────────────────────────────────────────────────────────────────

const ViewTab: React.FC<{
  frameworks: Framework[];
  toast: ReturnType<typeof useToast>["toast"];
}> = ({ frameworks, toast }) => {
  const [openFrameworks, setOpenFrameworks] = useState<Set<string>>(new Set());
  const [openChapters,   setOpenChapters]   = useState<Set<string>>(new Set());
  const [search,         setSearch]         = useState("");
  const [pending,        setPending]        = useState<PendingDelete | null>(null);
  const [deleting,       setDeleting]       = useState(false);

  const toggleFw = (id: string) => setOpenFrameworks(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleCh = (id: string) => setOpenChapters(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const confirmDelete = async () => {
    if (!pending) return;
    setDeleting(true);
    try {
      if (pending.type === "framework") {
        await fsDeleteFramework(pending.fwId);
        toast({ title: "Framework deleted", description: pending.label });
      } else if (pending.type === "chapter") {
        const fw = frameworks.find(f => f.id === pending.fwId)!;
        await fsSaveFramework({ ...fw, chapters: fw.chapters.filter(c => c.id !== pending.chId) });
        toast({ title: "Chapter deleted", description: pending.label });
      } else {
        const updated: Framework = {
          ...pending.fw,
          chapters: pending.fw.chapters.map(c =>
            c.id !== pending.chId ? c : { ...c, rules: c.rules.filter(r => r.id !== pending.ruleId) }
          ),
        };
        await fsSaveFramework(updated);
        toast({ title: "Rule deleted", description: pending.label });
      }
    } catch (e) {
      toast({ title: "Delete failed", description: String(e), variant: "destructive" });
    } finally {
      setDeleting(false);
      setPending(null);
    }
  };

  const q = search.toLowerCase();
  const filtered = frameworks.filter(f =>
    !q ||
    f.title.toLowerCase().includes(q) ||
    f.chapters.some(c =>
      c.title.toLowerCase().includes(q) ||
      c.rules.some(r => r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q))
    )
  );

  if (frameworks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <TreePine className="h-16 w-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-600 mb-2">No frameworks yet</h3>
        <p className="text-sm text-gray-400">Switch to "Add / Modify" to create your first framework</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <input
        className="w-full max-w-md border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        placeholder="Search frameworks, chapters or rules…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {/* ── Inline delete confirmation banner ── */}
      {pending && (
        <div className="flex items-center gap-4 bg-red-50 border border-red-200 rounded-lg px-5 py-3 shadow-sm">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">
              Delete {pending.type}?
            </p>
            <p className="text-xs text-red-600 truncate">
              "{pending.label}"
              {pending.type === "framework" && " — all chapters and rules inside will also be deleted."}
              {pending.type === "chapter"   && " — all rules inside will also be deleted."}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="border-gray-300 text-gray-600"
              onClick={() => setPending(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</>}
            </Button>
          </div>
        </div>
      )}

      {filtered.map(fw => {
        const cfg = colorMap[fw.color] || colorMap.blue;
        const chapterCount = fw.chapters.length;
        const ruleCount = fw.chapters.reduce((a, c) => a + c.rules.length, 0);
        const isOpen = openFrameworks.has(fw.id);

        return (
          <Card key={fw.id} className={cn("border-2 transition-shadow hover:shadow-md", cfg.border)}>
            {/* Framework row */}
            <div
              className={cn("flex items-center gap-3 p-4 cursor-pointer select-none rounded-t-lg", cfg.bg)}
              onClick={() => toggleFw(fw.id)}
            >
              <div className={cn("w-3 h-3 rounded-full flex-shrink-0", cfg.dot)} />
              {isOpen
                ? <FolderOpen className="h-5 w-5 text-gray-500 flex-shrink-0" />
                : <Folder     className="h-5 w-5 text-gray-500 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn("font-semibold text-base", cfg.text)}>{fw.title}</span>
                  <Badge variant="outline" className={cn("text-xs", cfg.badge)}>{chapterCount} chapter{chapterCount !== 1 ? "s" : ""}</Badge>
                  <Badge variant="outline" className={cn("text-xs", cfg.badge)}>{ruleCount} rule{ruleCount !== 1 ? "s" : ""}</Badge>
                </div>
                {fw.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{fw.description}</p>}
              </div>
              <button
                title="Delete framework"
                className="p-1.5 rounded-md bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-700 border border-red-200 transition-colors flex-shrink-0"
                onClick={e => { e.stopPropagation(); setPending({ type: "framework", fwId: fw.id, label: fw.title }); }}
              >
                <Trash2 className="h-4 w-4" />
              </button>
              {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
            </div>

            {/* Chapters */}
            {isOpen && (
              <div className="px-4 pb-4 space-y-3 pt-2">
                {fw.chapters.length === 0 ? (
                  <p className="text-sm text-gray-400 italic pl-6">No chapters yet</p>
                ) : fw.chapters.map(ch => {
                  const chOpen = openChapters.has(ch.id);
                  return (
                    <div key={ch.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Chapter row */}
                      <div
                        className="flex items-center gap-3 px-4 py-3 bg-white cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => toggleCh(ch.id)}
                      >
                        <BookMarked className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-800">{ch.title}</span>
                            <Badge variant="outline" className="text-xs text-gray-500">{ch.rules.length} rule{ch.rules.length !== 1 ? "s" : ""}</Badge>
                          </div>
                          {ch.description && <p className="text-xs text-gray-400 truncate">{ch.description}</p>}
                        </div>
                        <button
                          title="Delete chapter"
                          className="p-1 rounded-md bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-700 border border-red-200 transition-colors flex-shrink-0"
                          onClick={e => { e.stopPropagation(); setPending({ type: "chapter", fwId: fw.id, chId: ch.id, label: ch.title }); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        {chOpen ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                      </div>

                      {/* Rules */}
                      {chOpen && (
                        <div className="divide-y divide-gray-100 border-t border-gray-100">
                          {ch.rules.length === 0 ? (
                            <p className="text-xs text-gray-400 italic px-6 py-3">No rules yet</p>
                          ) : ch.rules.map((rule, idx) => (
                            <div key={rule.id} className="flex items-start gap-3 px-5 py-3 bg-gray-50 hover:bg-white transition-colors">
                              <span className="text-xs font-mono text-gray-400 mt-0.5 flex-shrink-0 w-6 text-right">{idx + 1}.</span>
                              <ScrollText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-gray-800">{rule.title}</span>
                                  {rule.reference && (
                                    <Badge variant="outline" className="text-xs text-gray-500 border-gray-300 font-mono">{rule.reference}</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{rule.description}</p>
                              </div>
                              <button
                                title="Delete rule"
                                className="p-1 rounded-md bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-700 border border-red-200 transition-colors flex-shrink-0 mt-0.5"
                                onClick={() => setPending({ type: "rule", fw, chId: ch.id, ruleId: rule.id, label: rule.title })}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// ─── ADD / MODIFY TAB ─────────────────────────────────────────────────────────

type AddAction = "framework" | "chapter" | "rule" | "pdf" | "seed" | "delete";

const AddTab: React.FC<{
  frameworks: Framework[];
  toast: ReturnType<typeof useToast>["toast"];
}> = ({ frameworks, toast }) => {
  const [action, setAction] = useState<AddAction>("framework");

  const actions: { id: AddAction; label: string; icon: React.ReactNode; desc: string; danger?: boolean }[] = [
    { id: "framework", label: "New Framework",    icon: <Layers    className="h-5 w-5" />, desc: "Top-level compliance category" },
    { id: "chapter",   label: "New Chapter",      icon: <BookOpen  className="h-5 w-5" />, desc: "Group of related rules" },
    { id: "rule",      label: "New Rule",          icon: <AlignLeft className="h-5 w-5" />, desc: "Single disclosure requirement" },
    { id: "pdf",       label: "Extract from PDF", icon: <Upload    className="h-5 w-5" />, desc: "AI extracts all disclosure rules" },
    { id: "seed",      label: "Load Demo Data",   icon: <Wand2     className="h-5 w-5" />, desc: "Seed a few frameworks to explore" },
    { id: "delete",    label: "Delete",            icon: <Trash2    className="h-5 w-5" />, desc: "Remove framework, chapter or rule", danger: true },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="space-y-2">
        {actions.map(a => (
          <button
            key={a.id}
            onClick={() => setAction(a.id)}
            className={cn(
              "w-full text-left flex items-start gap-3 px-4 py-3 rounded-lg border transition-all",
              action === a.id && a.danger  ? "bg-red-50 border-red-300 text-red-800 shadow-sm" :
              action === a.id             ? "bg-blue-50 border-blue-300 text-blue-800 shadow-sm" :
              a.danger                    ? "bg-white border-red-100 text-gray-700 hover:bg-red-50" :
                                            "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            )}
          >
            <span className={cn("mt-0.5 flex-shrink-0",
              action === a.id && a.danger ? "text-red-600" :
              action === a.id             ? "text-blue-600" :
              a.danger                    ? "text-red-400" : "text-gray-400"
            )}>{a.icon}</span>
            <div>
              <div className="text-sm font-medium">{a.label}</div>
              <div className="text-xs text-gray-400 mt-0.5">{a.desc}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="lg:col-span-3">
        {action === "framework" && <AddFrameworkForm toast={toast} />}
        {action === "chapter"   && <AddChapterForm   frameworks={frameworks} toast={toast} />}
        {action === "rule"      && <AddRuleForm      frameworks={frameworks} toast={toast} />}
        {action === "pdf"       && <PdfExtractForm   frameworks={frameworks} toast={toast} />}
        {action === "seed"      && <SeedDemoDataForm frameworks={frameworks} toast={toast} />}
        {action === "delete"    && <DeleteForm       frameworks={frameworks} toast={toast} />}
      </div>
    </div>
  );
};

// ─── Form: Add Framework ──────────────────────────────────────────────────────

const AddFrameworkForm: React.FC<{
  toast: ReturnType<typeof useToast>["toast"];
}> = ({ toast }) => {
  const [title, setTitle] = useState("");
  const [desc,  setDesc]  = useState("");
  const [color, setColor] = useState("blue");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast({ title: "Title required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      const fw: Framework = { id: uid(), title: title.trim(), description: desc.trim(), color, chapters: [], createdAt: new Date().toISOString() };
      await fsAddFramework(fw);
      toast({ title: "Framework created", description: fw.title });
      setTitle(""); setDesc(""); setColor("blue");
    } catch (e) {
      toast({ title: "Save failed", description: String(e), variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-blue-600" />New Framework</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <Field label="Framework Title *">
          <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. SEBI LODR, Ind AS, Companies Act 2013" />
        </Field>
        <Field label="Description">
          <Textarea className="resize-none" rows={3} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Brief description…" />
        </Field>
        <Field label="Colour Theme">
          <div className="flex gap-2 flex-wrap">
            {FRAMEWORK_COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)}
                className={cn("w-8 h-8 rounded-full border-4 transition-all", colorMap[c].dot, color === c ? "border-gray-800 scale-110" : "border-transparent")} />
            ))}
          </div>
        </Field>
        <Button onClick={submit} disabled={saving} className="w-full">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlusCircle className="h-4 w-4 mr-2" />}
          Create Framework
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── Form: Add Chapter ────────────────────────────────────────────────────────

const AddChapterForm: React.FC<{
  frameworks: Framework[];
  toast: ReturnType<typeof useToast>["toast"];
}> = ({ frameworks, toast }) => {
  const [fwId,  setFwId]  = useState(frameworks[0]?.id || "");
  const [title, setTitle] = useState("");
  const [desc,  setDesc]  = useState("");
  const [saving, setSaving] = useState(false);

  if (frameworks.length === 0) return <EmptyState msg="Create a Framework first." />;

  const submit = async () => {
    if (!fwId)          { toast({ title: "Select a framework", variant: "destructive" }); return; }
    if (!title.trim())  { toast({ title: "Title required", variant: "destructive" }); return; }
    const fw = frameworks.find(f => f.id === fwId)!;
    const ch: Chapter = { id: uid(), title: title.trim(), description: desc.trim(), rules: [] };
    setSaving(true);
    try {
      await fsSaveFramework({ ...fw, chapters: [...fw.chapters, ch] });
      toast({ title: "Chapter created", description: ch.title });
      setTitle(""); setDesc("");
    } catch (e) {
      toast({ title: "Save failed", description: String(e), variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-purple-600" />New Chapter</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <Field label="Framework *">
          <select className={inputCls} value={fwId} onChange={e => setFwId(e.target.value)}>
            {frameworks.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
          </select>
        </Field>
        <Field label="Chapter Title *">
          <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Regulation 33 – Financial Results" />
        </Field>
        <Field label="Description">
          <Textarea className="resize-none" rows={3} value={desc} onChange={e => setDesc(e.target.value)} placeholder="What does this chapter cover?" />
        </Field>
        <Button onClick={submit} disabled={saving} className="w-full">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlusCircle className="h-4 w-4 mr-2" />}
          Create Chapter
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── Form: Add Rule ───────────────────────────────────────────────────────────

// ─── Voice-enabled Textarea ───────────────────────────────────────────────────

declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

interface VoiceTextareaProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
}

const VoiceTextarea: React.FC<VoiceTextareaProps> = ({ value, onChange, placeholder, rows = 4, className }) => {
  const [listening,  setListening]  = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [interim,    setInterim]    = useState("");
  const recognitionRef  = useRef<any>(null);
  const baseTextRef     = useRef(value);       // text that existed before this recording session
  const sessionFinalRef = useRef("");          // finals accumulated within the current session

  // Keep baseText in sync when user types manually (not during recording)
  const handleManualChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    baseTextRef.current = e.target.value;
    onChange(e.target.value);
  };

  const isSupported = typeof window !== "undefined" &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const startListening = async () => {
    if (!isSupported) {
      setVoiceError("Speech recognition is not supported in this browser. Please use Google Chrome.");
      return;
    }

    setVoiceError(null);

    // ── Step 1: Explicitly request mic permission via getUserMedia.
    // This is what triggers the browser's native "Allow microphone?" dialog.
    // SpeechRecognition.start() on its own often skips the dialog and just
    // throws not-allowed if permission hasn't been granted yet.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Permission granted — release the stream immediately; SpeechRecognition
      // manages its own audio capture internally.
      stream.getTracks().forEach(t => t.stop());
    } catch (err: any) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setVoiceError(
          "Microphone access was denied. Please click 'Allow' when the browser asks, " +
          "or open your browser's site settings and enable the microphone for this page."
        );
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setVoiceError("No microphone found. Please connect a microphone and try again.");
      } else {
        setVoiceError(`Could not access microphone: ${err?.message || name}`);
      }
      return;
    }

    // ── Step 2: Permission granted — start SpeechRecognition.
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous      = true;
    recognition.interimResults  = true;
    recognition.lang            = "en-IN";
    recognition.maxAlternatives = 1;

    // Save current field value as the base before we start appending
    baseTextRef.current     = value;
    sessionFinalRef.current = "";
    setInterim("");

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event: any) => {
      let sessionFinal = "";
      let currentInterim = "";

      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          sessionFinal += event.results[i][0].transcript;
        } else {
          currentInterim += event.results[i][0].transcript;
        }
      }

      sessionFinalRef.current = sessionFinal;
      setInterim(currentInterim);

      const parts = [baseTextRef.current, sessionFinal, currentInterim].filter(Boolean);
      onChange(parts.join(" ").replace(/  +/g, " ").trimStart());
    };

    recognition.onerror = (event: any) => {
      const msgs: Record<string, string> = {
        "not-allowed":   "Microphone access denied. Please allow microphone access in your browser settings and try again.",
        "no-speech":     "No speech detected. Please speak clearly and try again.",
        "network":       "Network error during speech recognition. Check your internet connection.",
        "aborted":       "",  // user stopped — no message needed
        "audio-capture": "No microphone found. Please connect a microphone and try again.",
      };
      const msg = msgs[event.error] ?? `Speech recognition error: ${event.error}`;
      if (msg) setVoiceError(msg);
      setListening(false);
      setInterim("");
    };

    recognition.onend = () => {
      setListening(false);
      setInterim("");
      // Freeze the final composed text (without interim) so it stays in the field
      const parts = [baseTextRef.current, sessionFinalRef.current].filter(Boolean);
      const finalText = parts.join(" ").replace(/  +/g, " ").trimStart();
      onChange(finalText);
      baseTextRef.current = finalText;
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => { recognitionRef.current?.abort(); };
  }, []);

  return (
    <div className="space-y-1">
      <div className="relative">
        <Textarea
          className={cn(
            "resize-none pr-11 transition-all",
            listening && "border-red-400 ring-2 ring-red-100 bg-red-50/30",
            className,
          )}
          rows={rows}
          value={value}
          onChange={handleManualChange}
          placeholder={placeholder}
        />

        {/* Mic button — top-right corner */}
        <button
          type="button"
          title={!isSupported ? "Voice input requires Chrome" : listening ? "Stop recording" : "Start voice input"}
          onClick={listening ? stopListening : startListening}
          disabled={!isSupported}
          className={cn(
            "absolute top-2 right-2 flex items-center justify-center w-7 h-7 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-offset-1",
            !isSupported
              ? "bg-gray-100 text-gray-300 cursor-not-allowed"
              : listening
                ? "bg-red-500 text-white shadow-md animate-pulse focus:ring-red-400"
                : "bg-gray-100 text-gray-500 hover:bg-indigo-100 hover:text-indigo-600 focus:ring-indigo-300"
          )}
        >
          {listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Recording status */}
      {listening && (
        <div className="flex items-center gap-2 px-1">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
          </span>
          <span className="text-xs text-red-600 font-medium">
            Recording… speak your rule description. Click <MicOff className="inline h-3 w-3 mx-0.5" /> to stop.
          </span>
        </div>
      )}

      {/* Interim preview */}
      {listening && interim && (
        <p className="text-xs text-gray-400 italic px-1 truncate">
          Hearing: "{interim}"
        </p>
      )}

      {/* Error */}
      {voiceError && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">{voiceError}</p>
        </div>
      )}

      {/* Browser not supported */}
      {!isSupported && (
        <p className="text-xs text-gray-400 px-1 flex items-center gap-1">
          <MicOff className="h-3 w-3" /> Voice input requires Google Chrome
        </p>
      )}
    </div>
  );
};

// ─── Form: Add Rule ───────────────────────────────────────────────────────────

const AddRuleForm: React.FC<{
  frameworks: Framework[];
  toast: ReturnType<typeof useToast>["toast"];
}> = ({ frameworks, toast }) => {
  const [fwId,  setFwId]  = useState(frameworks[0]?.id || "");
  const fw = frameworks.find(f => f.id === fwId);
  const [chId,  setChId]  = useState(fw?.chapters[0]?.id || "");
  const [title, setTitle] = useState("");
  const [desc,  setDesc]  = useState("");
  const [ref,   setRef]   = useState("");
  const [prompt, setPrompt] = useState("");
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const f = frameworks.find(x => x.id === fwId);
    setChId(f?.chapters[0]?.id || "");
  }, [fwId, frameworks]);

  // Reset prompt when description changes significantly
  useEffect(() => { setPrompt(""); }, [desc]);

  if (frameworks.length === 0)           return <EmptyState msg="Create a Framework first." />;
  if (!fw || fw.chapters.length === 0)  return <EmptyState msg="Add a Chapter to this framework first." />;

  const handleGeneratePrompt = async () => {
    if (!title.trim() && !desc.trim()) { toast({ title: "Enter a title or description first", variant: "destructive" }); return; }
    setGeneratingPrompt(true);
    try {
      const generated = await generateRulePrompt(title.trim() || desc.trim(), desc.trim(), ref.trim());
      setPrompt(generated);
      toast({ title: "Prompt generated", description: "Review and edit if needed before saving." });
    } catch (e) {
      toast({ title: "Generation failed", description: String(e), variant: "destructive" });
    } finally { setGeneratingPrompt(false); }
  };

  const submit = async () => {
    if (!fwId || !chId)  { toast({ title: "Select framework and chapter", variant: "destructive" }); return; }
    if (!title.trim())   { toast({ title: "Title required", variant: "destructive" }); return; }
    if (!desc.trim())    { toast({ title: "Description required", variant: "destructive" }); return; }
    const rule: Rule = {
      id: uid(), title: title.trim(), description: desc.trim(),
      reference: ref.trim(), prompt: prompt.trim(),
      createdAt: new Date().toISOString(),
    };
    const updated: Framework = {
      ...fw!,
      chapters: fw!.chapters.map(c => c.id !== chId ? c : { ...c, rules: [...c.rules, rule] }),
    };
    setSaving(true);
    try {
      await fsSaveFramework(updated);
      toast({ title: "Rule added", description: rule.title });
      setTitle(""); setDesc(""); setRef(""); setPrompt("");
    } catch (e) {
      toast({ title: "Save failed", description: String(e), variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><AlignLeft className="h-5 w-5 text-green-600" />New Rule</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Framework *">
            <select className={inputCls} value={fwId} onChange={e => setFwId(e.target.value)}>
              {frameworks.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
            </select>
          </Field>
          <Field label="Chapter *">
            <select className={inputCls} value={chId} onChange={e => setChId(e.target.value)}>
              {(frameworks.find(f => f.id === fwId)?.chapters || []).map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Rule Title *">
          <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Disclosure of EPS for current and previous period" />
        </Field>
        <Field label="Description *">
          <VoiceTextarea
            value={desc}
            onChange={setDesc}
            rows={4}
            placeholder="Describe what must be disclosed or complied with… or click 🎤 to speak"
          />
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
            <Mic className="h-3 w-3 text-indigo-400" />
            Speak a rich, natural description — the AI uses this to generate a stronger compliance prompt.
          </p>
        </Field>
        <Field label="Reference / Section">
          <input className={inputCls} value={ref} onChange={e => setRef(e.target.value)} placeholder="e.g. Regulation 33(3)(a), Ind AS 33 Para 10" />
        </Field>

        {/* AI Prompt Generation */}
        <div className="border border-amber-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-amber-50">
            <span className="text-sm font-medium text-amber-800 flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Compliance Check Prompt
            </span>
            <Button
              size="sm" variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs"
              onClick={handleGeneratePrompt}
              disabled={generatingPrompt || (!title.trim() && !desc.trim())}
            >
              {generatingPrompt
                ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Generating…</>
                : <><Sparkles className="h-3 w-3 mr-1" />{prompt ? "Regenerate" : "Generate with AI"}</>
              }
            </Button>
          </div>
          <div className="p-3">
            <Textarea
              className="resize-none text-xs font-mono bg-white min-h-[100px]"
              rows={5}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="Click 'Generate with AI' to create a detailed compliance check prompt, or type one manually…"
            />
            {!prompt && (
              <p className="text-xs text-gray-400 mt-1">This prompt will be used in the Checklist runner to analyse documents against this rule.</p>
            )}
          </div>
        </div>

        <Button onClick={submit} disabled={saving} className="w-full">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlusCircle className="h-4 w-4 mr-2" />}
          Add Rule
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── Form: Load Demo Data ─────────────────────────────────────────────────────

const SeedDemoDataForm: React.FC<{
  frameworks: Framework[];
  toast: ReturnType<typeof useToast>["toast"];
}> = ({ frameworks, toast }) => {
  const [seeding, setSeeding] = useState(false);
  const [done, setDone] = useState(false);

  const run = async () => {
    setSeeding(true);
    try {
      const added = await seedDemoData(frameworks);
      setDone(true);
      toast({
        title: added > 0 ? `${added} demo framework${added === 1 ? "" : "s"} added` : "Demo data already loaded",
        description: added > 0 ? "SEBI LODR, Companies Act 2013 and Ind AS Standards are ready in the View tab." : undefined,
      });
    } catch (e) {
      toast({ title: "Seeding failed", description: String(e), variant: "destructive" });
    } finally { setSeeding(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-emerald-600" />Load Demo Data</CardTitle>
        <p className="text-sm text-gray-500">
          Seed a few realistic frameworks — SEBI LODR, Companies Act 2013, and Ind AS Standards
          (with Ind AS 16 pre-filled) — so the garden isn't empty while you explore. Safe to click
          more than once; it skips frameworks that already exist.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { title: "SEBI LODR", desc: "Regulation 33 — 3 rules", color: "text-blue-700 bg-blue-50 border-blue-200" },
            { title: "Companies Act 2013", desc: "Section 134 — 3 rules", color: "text-purple-700 bg-purple-50 border-purple-200" },
            { title: "Ind AS Standards", desc: "Ind AS 16 pre-filled — Ind AS 2 left empty for your live demo", color: "text-green-700 bg-green-50 border-green-200" },
          ].map(p => (
            <div key={p.title} className={cn("rounded-lg border p-3 text-sm", p.color)}>
              <div className="font-semibold">{p.title}</div>
              <div className="text-xs mt-1 opacity-80">{p.desc}</div>
            </div>
          ))}
        </div>
        <Button onClick={run} disabled={seeding} className="w-full">
          {seeding
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Seeding demo data…</>
            : done
              ? <><CheckCircle2 className="h-4 w-4 mr-2" />Loaded — run again to top up</>
              : <><Wand2 className="h-4 w-4 mr-2" />Load Demo Data</>}
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── Form: PDF Extract ────────────────────────────────────────────────────────

interface ExtractedWithSelection extends ExtractedRule { selected: boolean; tempId: string; prompt: string; }

const PdfExtractForm: React.FC<{
  frameworks: Framework[];
  toast: ReturnType<typeof useToast>["toast"];
}> = ({ frameworks, toast }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [pdfFile,        setPdfFile]        = useState<File | null>(null);
  const [stage,          setStage]          = useState<"idle" | "reading" | "detecting" | "extracting">("idle");
  const [detected,       setDetected]       = useState<{ key: string; std: StandardDef } | null>(null);
  const [extracted,      setExtracted]      = useState<ExtractedWithSelection[]>([]);
  const [fwId,           setFwId]           = useState(frameworks[0]?.id || "");
  const [chId,           setChId]           = useState("");
  const [newChapterTitle,setNewChapterTitle] = useState("");
  const [useNewChapter,  setUseNewChapter]  = useState(false);
  const [saving,         setSaving]         = useState(false);
  const busy = stage !== "idle";

  const fw = frameworks.find(f => f.id === fwId);

  useEffect(() => {
    const f = frameworks.find(x => x.id === fwId);
    setChId(f?.chapters[0]?.id || "");
    setUseNewChapter(!f || f.chapters.length === 0);
  }, [fwId, frameworks]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") { toast({ title: "PDF required", variant: "destructive" }); return; }
    setPdfFile(file);
    setExtracted([]);
    setDetected(null);
  };

  const wait = (ms: number) => new Promise(r => setTimeout(r, ms));

  const extract = async () => {
    if (!pdfFile) return;
    setExtracted([]); setDetected(null);
    const match = detectStandard(pdfFile.name);
    setStage("reading");
    try {
      await wait(650); setStage("detecting");
      await wait(650); setStage("extracting");

      if (match) {
        // Known standard — instant, reliable, curated checks (no live model call).
        await wait(500);
        setDetected(match);
        setExtracted(match.std.rules.map(r => ({ ...r, selected: true, tempId: uid() })));
        toast({ title: `Detected ${match.std.name}`, description: `${match.std.rules.length} rules extracted` });

        // point the save step at a sensible target: an existing chapter with this exact
        // name, else offer to create one under an "Ind AS" framework if present.
        const indAsFw = frameworks.find(f => /ind\s*-?\s*as/i.test(f.title));
        if (indAsFw) {
          setFwId(indAsFw.id);
          const ch = indAsFw.chapters.find(c => c.title === match.std.name);
          if (ch) { setUseNewChapter(false); setChId(ch.id); }
          else { setUseNewChapter(true); setNewChapterTitle(match.std.name); }
        } else {
          setUseNewChapter(true); setNewChapterTitle(match.std.name);
        }
      } else {
        // Unrecognised document — fall back to the live Gemini extraction.
        const b64 = await fileToBase64(pdfFile);
        const rules = await extractRulesFromPdf(b64);
        if (!rules.length) { toast({ title: "No rules found", variant: "destructive" }); setStage("idle"); return; }
        setExtracted(rules.map(r => ({ ...r, prompt: r.prompt || "", selected: true, tempId: uid() })));
        toast({ title: `${rules.length} rules extracted`, description: "Review and save below" });
      }
    } catch (e) {
      toast({ title: "Extraction failed", description: String(e), variant: "destructive" });
    } finally { setStage("idle"); }
  };

  const toggleAll = (val: boolean) => setExtracted(prev => prev.map(r => ({ ...r, selected: val })));
  const toggleOne = (id: string)   => setExtracted(prev => prev.map(r => r.tempId === id ? { ...r, selected: !r.selected } : r));

  const saveRules = async () => {
    const selected = extracted.filter(r => r.selected);
    if (!selected.length)                               { toast({ title: "Select at least one rule", variant: "destructive" }); return; }
    if (!fwId)                                          { toast({ title: "Select a framework",        variant: "destructive" }); return; }
    if (useNewChapter && !newChapterTitle.trim())       { toast({ title: "Enter chapter title",        variant: "destructive" }); return; }
    if (!useNewChapter && !chId)                        { toast({ title: "Select a chapter",           variant: "destructive" }); return; }

    const rules: Rule[] = selected.map(r => ({ id: uid(), title: r.title, description: r.description, reference: r.reference, prompt: r.prompt || "", createdAt: new Date().toISOString() }));
    const baseFw = frameworks.find(f => f.id === fwId)!;
    let updated: Framework;

    if (useNewChapter) {
      const ch: Chapter = { id: uid(), title: newChapterTitle.trim(), rules };
      updated = { ...baseFw, chapters: [...baseFw.chapters, ch] };
    } else {
      updated = { ...baseFw, chapters: baseFw.chapters.map(c => c.id !== chId ? c : { ...c, rules: [...c.rules, ...rules] }) };
    }

    setSaving(true);
    try {
      await fsSaveFramework(updated);
      toast({ title: `${rules.length} rules saved to Firestore`, description: useNewChapter ? `New chapter: ${newChapterTitle}` : undefined });
      setExtracted([]); setPdfFile(null); setNewChapterTitle(""); setDetected(null);
    } catch (e) {
      toast({ title: "Save failed", description: String(e), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const selectedCount = extracted.filter(r => r.selected).length;

  return (
    <div className="space-y-4">
      <Card className="border-indigo-100 bg-gradient-to-b from-indigo-50/40 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center shadow-sm shadow-indigo-200">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            Auto-extract rules from a document
          </CardTitle>
          <p className="text-sm text-gray-500">
            Upload a standard, circular or policy (e.g. an Ind AS) — a4i identifies it and proposes
            the compliance checks it implies. Anything unrecognised falls back to live AI extraction.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handleFile} />

          {pdfFile && !busy ? (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              <FileText className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="flex-1 text-sm font-medium text-green-800 truncate">{pdfFile.name}</span>
              <span className="text-xs text-green-600">{(pdfFile.size / 1024 / 1024).toFixed(1)} MB</span>
              <button onClick={() => { setPdfFile(null); setExtracted([]); setDetected(null); }} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
            </div>
          ) : !pdfFile ? (
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-indigo-200 hover:border-blue-400 bg-indigo-50/40 rounded-lg py-10 flex flex-col items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors"
            >
              <Upload className="h-8 w-8 text-indigo-400" />
              <span className="text-sm font-medium">Click to upload a document</span>
              <span className="text-xs text-gray-400">SEBI circulars, Ind AS standards, audit guidelines…</span>
            </button>
          ) : null}

          {pdfFile && !busy && (
            <Button onClick={extract} className="w-full">
              <Sparkles className="h-4 w-4 mr-2" />Extract Rules
            </Button>
          )}

          {busy && (
            <div className="py-5 text-center">
              <div className="relative w-12 h-12 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-600 to-indigo-500 shadow-lg shadow-indigo-200 animate-[bounce_1.6s_ease-in-out_infinite]" />
                <div className="absolute -inset-1.5 rounded-full border-2 border-indigo-300 animate-ping" />
              </div>
              <div className="text-xs text-gray-400 mb-3 truncate max-w-xs mx-auto">{pdfFile?.name}</div>
              <div className="flex flex-col gap-1.5 max-w-xs mx-auto text-left">
                {([
                  { key: "reading", label: "Reading document", icon: FileText },
                  { key: "detecting", label: "Identifying the standard", icon: Search },
                  { key: "extracting", label: "Extracting compliance rules", icon: Sparkles },
                ] as const).map((s, i) => {
                  const order = ["reading", "detecting", "extracting"];
                  const idx = order.indexOf(stage);
                  const done = i < idx, current = i === idx;
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className={cn(
                      "flex items-center gap-2 text-sm px-2.5 py-1.5 rounded-md transition-colors",
                      current ? "bg-indigo-50 text-indigo-700 font-medium" : done ? "text-green-600" : "text-gray-400"
                    )}>
                      {done ? <CheckCircle2 className="h-4 w-4" /> : <Icon className={cn("h-4 w-4", current && "animate-pulse")} />}
                      <span>{s.label}{current ? "…" : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extracted rules review */}
      <AnimatePresence>
        {extracted.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            {detected && (
              <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 mb-3">
                <Sparkles className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-indigo-900">Detected: {detected.std.name}</div>
                  <div className="text-xs text-indigo-700 mt-0.5">{detected.std.summary}</div>
                </div>
              </div>
            )}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base">{extracted.length} Rules Extracted — Review &amp; Select</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toggleAll(true)}>Select all</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleAll(false)}>Deselect all</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {extracted.map((r, idx) => (
                  <motion.div
                    key={r.tempId}
                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.22, delay: idx * 0.04 }}
                    onClick={() => toggleOne(r.tempId)}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                      r.selected ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200 opacity-60"
                    )}
                  >
                    <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5", r.selected ? "bg-blue-600 border-blue-600" : "border-gray-300 bg-white")}>
                      {r.selected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-400 font-mono">#{idx + 1}</span>
                        <span className="text-sm font-medium text-gray-800">{r.title}</span>
                        {r.reference && <Badge variant="outline" className="text-xs font-mono">{r.reference}</Badge>}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{r.description}</p>
                    </div>
                  </motion.div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save destination */}
      {extracted.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Save {selectedCount} Rule{selectedCount !== 1 ? "s" : ""} to Firestore</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {frameworks.length === 0 ? (
              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                Create a Framework first in the "New Framework" section.
              </div>
            ) : (
              <>
                <Field label="Framework *">
                  <select className={inputCls} value={fwId} onChange={e => setFwId(e.target.value)}>
                    {frameworks.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
                  </select>
                </Field>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="radio" checked={!useNewChapter} onChange={() => setUseNewChapter(false)} disabled={!fw || fw.chapters.length === 0} />
                    Existing Chapter
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="radio" checked={useNewChapter} onChange={() => setUseNewChapter(true)} />
                    Create New Chapter
                  </label>
                </div>

                {!useNewChapter ? (
                  <Field label="Chapter *">
                    <select className={inputCls} value={chId} onChange={e => setChId(e.target.value)}>
                      {(fw?.chapters || []).map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                  </Field>
                ) : (
                  <Field label="New Chapter Title *">
                    <input className={inputCls} value={newChapterTitle} onChange={e => setNewChapterTitle(e.target.value)} placeholder="e.g. Disclosure Requirements" />
                  </Field>
                )}

                <Button onClick={saveRules} disabled={saving || selectedCount === 0} className="w-full">
                  {saving
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving to Firestore…</>
                    : <><Check className="h-4 w-4 mr-2" />Save {selectedCount} Rule{selectedCount !== 1 ? "s" : ""} to Firestore</>}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── Form: Delete ─────────────────────────────────────────────────────────────

type DeleteTarget = "framework" | "chapter" | "rule";

const DeleteForm: React.FC<{
  frameworks: Framework[];
  toast: ReturnType<typeof useToast>["toast"];
}> = ({ frameworks, toast }) => {
  const [target,   setTarget]   = useState<DeleteTarget>("rule");
  const [fwId,     setFwId]     = useState(frameworks[0]?.id || "");
  const [chId,     setChId]     = useState("");
  const [ruleId,   setRuleId]   = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [deleting,  setDeleting]  = useState(false);

  const fw = frameworks.find(f => f.id === fwId);
  const ch = fw?.chapters.find(c => c.id === chId);

  // Reset downstream selections when parent changes
  useEffect(() => { setChId(fw?.chapters[0]?.id || ""); setRuleId(""); setConfirmed(false); }, [fwId]);
  useEffect(() => { setRuleId(ch?.rules[0]?.id || ""); setConfirmed(false); }, [chId]);
  useEffect(() => { setConfirmed(false); }, [target, fwId, chId, ruleId]);

  if (frameworks.length === 0) return <EmptyState msg="Nothing to delete — create a Framework first." />;

  const itemLabel =
    target === "framework" ? fw?.title :
    target === "chapter"   ? ch?.title :
    ch?.rules.find(r => r.id === ruleId)?.title || "";

  const canDelete =
    (target === "framework" && !!fwId) ||
    (target === "chapter"   && !!fwId && !!chId) ||
    (target === "rule"      && !!fwId && !!chId && !!ruleId);

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      if (target === "framework") {
        await fsDeleteFramework(fwId);
        toast({ title: "Framework deleted", description: itemLabel });
        setFwId(frameworks.find(f => f.id !== fwId)?.id || "");
      } else if (target === "chapter") {
        await fsSaveFramework({ ...fw!, chapters: fw!.chapters.filter(c => c.id !== chId) });
        toast({ title: "Chapter deleted", description: itemLabel });
      } else {
        const updated: Framework = {
          ...fw!,
          chapters: fw!.chapters.map(c =>
            c.id !== chId ? c : { ...c, rules: c.rules.filter(r => r.id !== ruleId) }
          ),
        };
        await fsSaveFramework(updated);
        toast({ title: "Rule deleted", description: itemLabel });
        setRuleId(ch?.rules.find(r => r.id !== ruleId)?.id || "");
      }
      setConfirmed(false);
    } catch (e) {
      toast({ title: "Delete failed", description: String(e), variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="border-red-200">
      <CardHeader className="bg-red-50 rounded-t-lg border-b border-red-100">
        <CardTitle className="flex items-center gap-2 text-red-700">
          <Trash2 className="h-5 w-5" /> Delete
        </CardTitle>
        <p className="text-xs text-red-500 mt-1">Deletions are permanent and cannot be undone.</p>
      </CardHeader>
      <CardContent className="space-y-4 pt-5">

        {/* What to delete */}
        <Field label="What do you want to delete?">
          <div className="flex gap-3">
            {(["framework", "chapter", "rule"] as DeleteTarget[]).map(t => (
              <label key={t} className={cn(
                "flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm font-medium transition-all capitalize",
                target === t ? "bg-red-50 border-red-300 text-red-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"
              )}>
                <input type="radio" className="hidden" checked={target === t} onChange={() => setTarget(t)} />
                {t}
              </label>
            ))}
          </div>
        </Field>

        {/* Framework selector */}
        <Field label="Framework *">
          <select className={inputCls} value={fwId} onChange={e => setFwId(e.target.value)}>
            {frameworks.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
          </select>
        </Field>

        {/* Chapter selector */}
        {(target === "chapter" || target === "rule") && (
          <Field label="Chapter *">
            {!fw || fw.chapters.length === 0
              ? <p className="text-xs text-gray-400 italic">No chapters in this framework.</p>
              : <select className={inputCls} value={chId} onChange={e => setChId(e.target.value)}>
                  {fw.chapters.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
            }
          </Field>
        )}

        {/* Rule selector */}
        {target === "rule" && chId && (
          <Field label="Rule *">
            {!ch || ch.rules.length === 0
              ? <p className="text-xs text-gray-400 italic">No rules in this chapter.</p>
              : <select className={inputCls} value={ruleId} onChange={e => setRuleId(e.target.value)}>
                  {ch.rules.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
                </select>
            }
          </Field>
        )}

        {/* Warning box */}
        {canDelete && itemLabel && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">
                  You are about to permanently delete the {target}:
                </p>
                <p className="text-sm text-red-700 font-mono mt-1">"{itemLabel}"</p>
                {target === "framework" && (
                  <p className="text-xs text-red-600 mt-1">
                    This will also delete all {fw?.chapters.length || 0} chapter(s) and all their rules.
                  </p>
                )}
                {target === "chapter" && (
                  <p className="text-xs text-red-600 mt-1">
                    This will also delete all {ch?.rules.length || 0} rule(s) inside this chapter.
                  </p>
                )}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                className="accent-red-600"
              />
              <span className="text-sm text-red-700 font-medium">Yes, I understand this cannot be undone</span>
            </label>
          </div>
        )}

        <Button
          className="w-full bg-red-600 hover:bg-red-700 text-white"
          disabled={!canDelete || !confirmed || deleting}
          onClick={handleDelete}
        >
          {deleting
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting…</>
            : <><Trash2 className="h-4 w-4 mr-2" />Delete {target}</>
          }
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    {children}
  </div>
);

const EmptyState: React.FC<{ msg: string }> = ({ msg }) => (
  <Card>
    <CardContent className="flex items-center gap-3 py-10 justify-center">
      <AlertCircle className="h-5 w-5 text-amber-500" />
      <p className="text-sm text-gray-600">{msg}</p>
    </CardContent>
  </Card>
);

export default ComplianceGarden;
